import React, { useState } from "react";
import styled from "styled-components";
import {
  FaEnvelope,
  FaClock,
  FaCheckCircle,
  FaArrowRight,
  FaCalendarAlt,
} from "react-icons/fa";
import bgImage from "../assets/images/contact-bg.jpeg";
import emailjs from "emailjs-com";
import ReCAPTCHA from "react-google-recaptcha";
import { track } from "../lib/tracking";

/* ─── styled ─── */

const Section = styled.section`
  padding: 100px 0;
  background-image: url(${bgImage});
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
  color: white;
  position: relative;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(
      135deg,
      rgba(10, 12, 26, 0.92) 0%,
      rgba(15, 17, 35, 0.88) 100%
    );
  }

  @media (max-width: 900px) {
    padding: 60px 0;
    background-attachment: scroll;
  }
`;

const Inner = styled.div`
  position: relative;
  z-index: 1;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 40px;

  @media (max-width: 900px) {
    padding: 0 20px;
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 56px;
`;

const SectionTag = styled.span`
  display: inline-block;
  background: rgba(0, 200, 255, 0.08);
  border: 1px solid rgba(0, 200, 255, 0.2);
  color: #00c8ff;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 2px;
  text-transform: uppercase;
  padding: 8px 20px;
  border-radius: 50px;
  margin-bottom: 24px;
`;

const SectionTitle = styled.h2`
  font-size: 2.8em;
  color: #ffffff;
  font-weight: 800;
  letter-spacing: -0.5px;
  margin-bottom: 16px;

  span {
    background: linear-gradient(135deg, #00c8ff, #005bea);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  @media (max-width: 900px) {
    font-size: 2em;
  }
`;

const SectionSubtitle = styled.p`
  color: rgba(255, 255, 255, 0.45);
  font-size: 1.1em;
  max-width: 500px;
  margin: 0 auto;
  line-height: 1.7;

  @media (max-width: 900px) {
    font-size: 1em;
  }
`;

const Sides = styled.div`
  display: flex;
  gap: 56px;

  @media (max-width: 900px) {
    flex-direction: column;
    gap: 40px;
  }
`;

/* ── left ── */

const Left = styled.div`
  flex: 1;
`;

const LeftTitle = styled.h3`
  font-size: 1.5em;
  font-weight: 800;
  color: #ffffff;
  margin-bottom: 16px;
`;

const LeftText = styled.p`
  font-size: 15px;
  color: rgba(255, 255, 255, 0.55);
  line-height: 1.75;
  margin-bottom: 32px;

  @media (max-width: 900px) {
    text-align: left;
  }
`;

const InfoCard = styled.div`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 16px;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
    border-color: rgba(0, 200, 255, 0.15);
  }
`;

const InfoIcon = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== "iconColor",
})`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: ${({ iconColor }) => iconColor || "#00c8ff"}12;
  border: 1px solid ${({ iconColor }) => iconColor || "#00c8ff"}20;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ iconColor }) => iconColor || "#00c8ff"};
  font-size: 16px;
  flex-shrink: 0;
`;

const InfoText = styled.div``;

const InfoLabel = styled.p`
  font-size: 11px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.3);
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 2px;
`;

const InfoValue = styled.p`
  font-size: 15px;
  color: #ffffff;
  font-weight: 600;

  a {
    color: inherit;
    text-decoration: none;
    transition: color 0.3s ease;
    &:hover {
      color: #00c8ff;
    }
  }
`;

/* ── promises ── */

const PromisesRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 28px;
`;

const CallCta = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  margin-top: 28px;
  background: linear-gradient(135deg, #00c8ff 0%, #005bea 100%);
  color: #ffffff;
  text-decoration: none;
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0.4px;
  padding: 15px 30px;
  border-radius: 12px;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 32px rgba(0, 200, 255, 0.3);
  }
`;

const CallCtaHint = styled.p`
  margin: 12px 0 0;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.4);
`;

const PromiseItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;

  svg {
    color: #10b981;
    font-size: 14px;
    flex-shrink: 0;
  }

  span {
    font-size: 14px;
    color: rgba(255, 255, 255, 0.5);
  }
`;

/* ── right: form ── */

const Right = styled.div`
  flex: 1;
`;

const FormCard = styled.div`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 20px;
  padding: 36px 32px;

  @media (max-width: 600px) {
    padding: 28px 20px;
  }
`;

const FormTitle = styled.h4`
  font-size: 16px;
  font-weight: 700;
  color: #ffffff;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 8px;

  svg {
    color: #00c8ff;
    font-size: 14px;
  }
`;

const Form = styled.form`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const Input = styled.input`
  padding: 16px 18px;
  font-size: 14px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.04);
  color: #ffffff;
  border-radius: 12px;
  transition: all 0.3s ease;
  font-family: inherit;
  grid-column: span ${(props) => (props.fullwidth ? 2 : 1)};

  &::placeholder {
    color: rgba(255, 255, 255, 0.3);
  }

  &:focus {
    outline: none;
    border-color: rgba(0, 200, 255, 0.4);
    background: rgba(0, 200, 255, 0.04);
  }

  @media (max-width: 600px) {
    grid-column: span 1;
  }
`;

const TextArea = styled.textarea`
  padding: 16px 18px;
  font-size: 14px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.04);
  color: #ffffff;
  border-radius: 12px;
  grid-column: span 2;
  height: 130px;
  resize: none;
  font-family: inherit;
  transition: all 0.3s ease;

  &::placeholder {
    color: rgba(255, 255, 255, 0.3);
  }

  &:focus {
    outline: none;
    border-color: rgba(0, 200, 255, 0.4);
    background: rgba(0, 200, 255, 0.04);
  }

  @media (max-width: 600px) {
    grid-column: span 1;
  }
`;

const CaptchaWrapper = styled.div`
  grid-column: span 2;

  @media (max-width: 600px) {
    grid-column: span 1;
  }
`;

const SubmitButton = styled.button`
  grid-column: span 2;
  background: linear-gradient(135deg, #00c8ff 0%, #005bea 100%);
  color: white;
  border: none;
  padding: 16px;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  text-transform: uppercase;
  letter-spacing: 1px;
  border-radius: 12px;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  svg {
    font-size: 12px;
    transition: transform 0.3s ease;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 30px rgba(0, 200, 255, 0.25);
    svg {
      transform: translateX(4px);
    }
  }

  @media (max-width: 600px) {
    grid-column: span 1;
  }
`;

const SuccessMessage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #34d399;
  text-align: center;
  margin-top: 20px;
  font-weight: 600;
  font-size: 14px;

  svg {
    font-size: 16px;
  }
`;

/* ─── component ─── */

export const Contact = () => {
  const [formData, setFormData] = useState({
    email: "",
    subject: "",
    message: "",
    telefono: "",
  });
  const [successMessage, setSuccessMessage] = useState("");
  const [captchaToken, setCaptchaToken] = useState(null);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const verificarCaptcha = async () => {
    const response = await fetch("/emailjs/verify-captcha", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ captchaToken }),
    });
    const result = await response.json();
    return result.success;
  };

  const sendEmail = async (e) => {
    e.preventDefault();

    if (formData.telefono) {
      console.log("Bot detectado");
      return;
    }

    if (!captchaToken) {
      alert("Por favor completa el CAPTCHA.");
      return;
    }

    const captchaValido = await verificarCaptcha();
    if (!captchaValido) {
      alert("Error validando CAPTCHA. Intenta nuevamente.");
      return;
    }

    emailjs
      .send(
        "service_hz3vepe",
        "template_19hrrqk",
        formData,
        "vEpAM_4evm5xbt-Zm"
      )
      .then(
        (result) => {
          console.log("Email enviado:", result.text);
          // Conversión de formulario. La reserva en Calendly dispara "Schedule",
          // así que en Meta quedan como dos eventos distinguibles.
          track("Lead", { content_name: "contact_form" });
          setSuccessMessage("¡Mensaje enviado con éxito!");
          setFormData({ email: "", subject: "", message: "" });
          setCaptchaToken(null);
        },
        (error) => {
          console.log("Error al enviar email:", error.text);
        }
      );
  };

  return (
    <Section id="contact">
      <Inner>
        <Header>
          <SectionTag>Hablemos</SectionTag>
          <SectionTitle>
            Inicia tu <span>Proyecto</span>
          </SectionTitle>
          <SectionSubtitle>
            Cuéntanos tu idea y te respondemos en menos de 24 horas con una
            propuesta personalizada.
          </SectionSubtitle>
        </Header>

        <Sides>
          <Left>
            <LeftTitle>¿Tienes un proyecto en mente?</LeftTitle>
            <LeftText>
              Ya sea una app móvil, una plataforma web o una solución con
              inteligencia artificial — estamos listos para ayudarte a
              construirlo. Escríbenos y agendamos una conversación sin
              compromiso.
            </LeftText>

            <InfoCard>
              <InfoIcon iconColor="#00c8ff">
                <FaEnvelope />
              </InfoIcon>
              <InfoText>
                <InfoLabel>Email</InfoLabel>
                <InfoValue>
                  <a href="mailto:mseguel@monaco-it-services.cl">
                    mseguel@monaco-it-services.cl
                  </a>
                </InfoValue>
              </InfoText>
            </InfoCard>

            <InfoCard>
              <InfoIcon iconColor="#f59e0b">
                <FaClock />
              </InfoIcon>
              <InfoText>
                <InfoLabel>Tiempo de respuesta</InfoLabel>
                <InfoValue>Menos de 24 horas</InfoValue>
              </InfoText>
            </InfoCard>

            <PromisesRow>
              <PromiseItem>
                <FaCheckCircle />
                <span>Consulta inicial sin costo</span>
              </PromiseItem>
              <PromiseItem>
                <FaCheckCircle />
                <span>Propuesta personalizada en 48 hrs</span>
              </PromiseItem>
              <PromiseItem>
                <FaCheckCircle />
                <span>Sin compromiso de permanencia</span>
              </PromiseItem>
            </PromisesRow>

            <CallCta href="/call">
              <FaCalendarAlt /> Agenda 30 minutos
            </CallCta>
            <CallCtaHint>
              ¿Prefieres hablar antes de escribir? Elige un horario y lo vemos en
              vivo.
            </CallCtaHint>
          </Left>

          <Right>
            <FormCard>
              <FormTitle>
                <FaArrowRight /> Envíanos un mensaje
              </FormTitle>
              <Form onSubmit={sendEmail}>
                <Input
                  type="email"
                  name="email"
                  placeholder="Tu email"
                  onChange={handleChange}
                  value={formData.email}
                />
                <Input
                  type="text"
                  name="subject"
                  placeholder="Asunto"
                  onChange={handleChange}
                  value={formData.subject}
                />
                <Input
                  type="text"
                  name="telefono"
                  placeholder="Tu teléfono"
                  style={{ display: "none" }}
                  onChange={handleChange}
                />
                <TextArea
                  name="message"
                  placeholder="Cuéntanos sobre tu proyecto..."
                  onChange={handleChange}
                  value={formData.message}
                />
                <CaptchaWrapper>
                  <ReCAPTCHA
                    sitekey={"6LdXoRMrAAAAAJJCgtloZHjJ_ZpKwVQVuOhNW8Hb"}
                    onChange={(token) => setCaptchaToken(token)}
                    theme="dark"
                  />
                </CaptchaWrapper>
                <SubmitButton type="submit">
                  Enviar Mensaje <FaArrowRight />
                </SubmitButton>
              </Form>
              {successMessage && (
                <SuccessMessage>
                  <FaCheckCircle />
                  {successMessage}
                </SuccessMessage>
              )}
            </FormCard>
          </Right>
        </Sides>
      </Inner>
    </Section>
  );
};
